import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import hotelService from '../services/hotelService';
import pricingService from '../services/pricingService';
import vaultPaymentService from '../services/vaultPaymentService';
import operaCloudCompareService from '../services/operaCloudCompareService';
import useCredentialStep from './useCredentialStep';
import WizardStepper from './WizardStepper';
import WizardHotelStep from './WizardHotelStep';
import CredentialChip from './shared/CredentialChip';
import CredentialsPicker from './shared/CredentialsPicker';
import CredentialSelector from './shared/CredentialSelector';
import WizardPaymentStep from './WizardPaymentStep';
import './AddPaymentPorterWizard.css';
import './OperaCloudCompare.css';

// Create-comparison wizard for "Opera Cloud v Opera Cloud" — Hotel → Credentials → Payment,
// mirroring the Payment Porter wizard.
//
// Credentials: two OHIP credentials (source + target). Each is chosen through the shared
// full-screen picker and driven by its own useCredentialStep instance, so a partner/chain-level
// credential prompts for the per-hotel override settings (HOTELID etc.) exactly like the sync
// task and payment-porter credential steps.
// Payment: the shared PaymentModal, pointed at the ohip-v-ohip product (free-seat / subscription /
// invoice via Stripe). On success the comparison is created.

const STEPS = [
    { num: 1, label: 'Hotel' },
    { num: 2, label: 'Credentials' },
    { num: 3, label: 'Payment' },
];

const OHIP_MARKETPLACE = { id: 37, name: 'Oracle OHIP' };   // OHIP marketplace (credentials source/target)
const PRODUCT_ID       = 8;              // v2_products.id for 'ohip-v-ohip'
const PRICING_TYPE     = 'ohip-v-ohip';


const OperaCloudCompareWizard = () => {
    const navigate = useNavigate();
    const { id: resumeId } = useParams();   // present when resuming payment for an existing comparison
    const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
    const { isSuperAdmin, isPartner, user } = useAuth();

    const [step, setStep] = useState(1);
    const [hotels, setHotels] = useState([]);
    const [hotelsLoading, setHotelsLoading] = useState(false);
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [pickerRole, setPickerRole] = useState(null); // null | 'source' | 'target'
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [awaitingPayment, setAwaitingPayment] = useState(false);
    const [invoiceResult, setInvoiceResult] = useState(null);
    const [createdCompareId, setCreatedCompareId] = useState(null);

    const invoicePrivilege = (isSuperAdmin || isPartner) ? 'partner' : isChainAdmin ? 'chain' : 'hotel';

    // One credential-step controller per instance. Both target the OHIP marketplace so each loads
    // the same override defs (HOTELID etc.) and manages its own selection independently.
    const credOpts = {
        selectedMarketplace: OHIP_MARKETPLACE,
        selectedHotel,
        selectedAccount,
        selectedPartner,
        solePartnerId: selectedPartner,
        isSuperAdmin,
        isPartner,
        isChainAdmin,
    };
    const csSource = useCredentialStep(credOpts);
    const csTarget = useCredentialStep(credOpts);

    const loadHotels = useCallback(async () => {
        setHotelsLoading(true);
        try {
            const res = await hotelService.getAllHotels(
                selectedAccount ? { accountid: selectedAccount } : { partnerid: selectedPartner }
            );
            setHotels(res.hotels || []);
        } catch (err) {
            console.error('Failed to load hotels', err);
        } finally {
            setHotelsLoading(false);
        }
    }, [selectedAccount]);

    useEffect(() => { loadHotels(); }, [loadHotels]);

    // Load the OHIP marketplace setting defs once so overrideDefs (HOTELID etc.) are available for
    // both instances the moment a chain/partner-level credential is picked.
    useEffect(() => {
        csSource.loadForMarketplace();
        csTarget.loadForMarketplace();
    }, []); // eslint-disable-line

    // Resume-payment mode: an existing (unpaid) comparison was opened from the list. Load it, seed
    // the hotel context from the saved record, and jump straight to the payment step. Payment then
    // updates the existing record instead of creating a new one (see handlePaymentComplete).
    useEffect(() => {
        if (!resumeId) return;
        (async () => {
            try {
                const res = await operaCloudCompareService.get(resumeId);
                const c = res.data?.comparison;
                if (!c) { navigate('/apps/opera-cloud-compare'); return; }
                setSelectedHotel({ hotelid: c.hotel_id, hotelname: c.hotel_name, accountid: c.account_id });
                setCreatedCompareId(c.id);
                setStep(3);
            } catch (err) {
                setErrors({ submit: err.response?.data?.message || 'Failed to load comparison' });
            }
        })();
    }, [resumeId]); // eslint-disable-line

    const goToCredentials = () => {
        if (!selectedHotel) { setErrors({ hotel: 'Please select a hotel' }); return; }
        setErrors({});
        setStep(2);
    };

    // Effective identity of an instance: the credential plus the property it resolves to (HOTELID
    // override for chain/partner-level creds). Two sides sharing this key point at the same instance.
    const instanceKey = (cs) => {
        const id = cs.selectedCredential?.credential_id;
        if (id == null) return null;
        return `${id}::${(cs.hotelIdOverride || '').trim().toLowerCase()}`;
    };

    const goToPayment = () => {
        const errs = {};
        if (!csSource.selectedCredential || !csTarget.selectedCredential) {
            errs.creds = 'Select both a source and a target instance';
        }
        const sv = csSource.validate();
        if (sv?.hotelIdOverride) errs.sourceOverride = sv.hotelIdOverride;
        const tv = csTarget.validate();
        if (tv?.hotelIdOverride) errs.targetOverride = tv.hotelIdOverride;
        const sk = instanceKey(csSource);
        const tk = instanceKey(csTarget);
        if (!errs.creds && sk && sk === tk) {
            errs.creds = 'Source and target must be different — they currently point at the same instance.';
        }
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setErrors({});
        setStep(3);
    };

    // Reset a picked credential's per-hotel overrides.
    const resetOverrides = (cs) => {
        cs.setHotelIdOverride('');
        const fields = {};
        cs.overrideDefs.forEach(d => { fields[d.SETTINGNAME] = ''; });
        cs.setCredentialOverrides(fields);
    };

    const handleSelect = (cs, cred, values) => {
        resetOverrides(cs);
        cs.pickWithOverrides(cred, values);
        setPickerRole(null);
    };

    const handleRemove = (cs) => {
        cs.setSelectedCredential(null);
        resetOverrides(cs);
    };

    // Payment service for the ohip-v-ohip product — prices come from the generic pricing resolver
    // (keyed by product type), payment methods / billing contacts from the shared vault-payment
    // endpoint. The backend pricing/config for 'ohip-v-ohip' (product 8) drives what is offered.
    const buildPaymentService = () => ({
        resolve: async () => {
            const [priceRes, vaultRes, seatRes] = await Promise.all([
                pricingService.resolve(PRICING_TYPE, selectedHotel?.hotelid),
                vaultPaymentService.resolve(selectedHotel?.hotelid, selectedAccount),
                pricingService.getFreeSeatCount(selectedHotel?.hotelid, PRICING_TYPE),
            ]);
            // /pricing/resolve returns { pricing: { upfront, annual, monthly, source } }.
            const p = priceRes.data?.pricing || {};
            const prices = [
                p.monthly && { id: p.monthly.price_id, amount: p.monthly.unit_amount / 100, currency: p.monthly.currency, recurring: 'month', lookup_key: p.monthly.lookup_key },
                p.annual  && { id: p.annual.price_id,  amount: p.annual.unit_amount  / 100, currency: p.annual.currency,  recurring: 'year',  lookup_key: p.annual.lookup_key  },
            ].filter(Boolean);
            // /pricing/free-seats returns { hotelSeats, chainSeats } for this hotel + product.
            const hotelSeats = seatRes.data?.hotelSeats ?? 0;
            const chainSeats = seatRes.data?.chainSeats ?? 0;
            return {
                data: {
                    prices,
                    upfrontPrice:    p.upfront ? { price_id: p.upfront.price_id, unit_amount: p.upfront.unit_amount, currency: p.upfront.currency } : null,
                    paymentMethods:  vaultRes.data?.paymentMethods  || [],
                    billingContacts: vaultRes.data?.billingContacts || null,
                    freeSeats:       hotelSeats + chainSeats,
                    freeSeatLevel:   hotelSeats > 0 ? 'hotel' : (chainSeats > 0 ? 'chain' : null),
                },
            };
        },
        createSetupIntent: (acctId, acctName, hId) => vaultPaymentService.createSetupIntent(acctId, acctName, hId),
        // createSubscription / useFreeSeat intentionally omitted (as in Payment Porter): the modal
        // returns the result and the comparison is created — and the seat consumed / subscription
        // started — server-side in handlePaymentComplete once the create endpoint exists.
    });

    // Builds the credential payload (credential_id, owning entity, per-hotel overrides) for one instance.
    const instancePayload = (cs) => {
        const p = cs.buildCredentialPayload();
        return {
            credential_id:   p.credential_id,
            account_id:      p.credential_account_id,
            hotel_id:        p.credential_hotel_id,
            partner_id:      p.credential_partner_id,
            hotel_id_override: cs.hotelIdOverride || null,
            overrides:       p.credential_overrides,
        };
    };

    // The chain/account for billing + ownership. In the Admin /apps flow no chain is selected in the
    // navbar, so fall back to the chosen hotel's own account.
    const effectiveAccountId = selectedAccount || selectedHotel?.accountid || null;

    // Base create payload — the comparison identity plus both instances. Payment-type-specific
    // flags (active / paid / use_free_seat / stripe_subscription_id) are merged per branch below.
    const buildComparePayload = () => ({
        name:           `${csSource.selectedCredential?.credential_name} → ${csTarget.selectedCredential?.credential_name}`,
        hotel_id:       selectedHotel?.hotelid || null,
        account_id:     effectiveAccountId,
        marketplace_id: OHIP_MARKETPLACE.id,
        source:         instancePayload(csSource),
        target:         instancePayload(csTarget),
    });

    // Called after PaymentModal completes. Mirrors the Payment Porter flow: free-seat and
    // subscription create an active, paid comparison; invoice creates an inactive/unpaid one and
    // then raises the Stripe invoice, moving the wizard to an "awaiting payment" state.
    const handlePaymentComplete = async (result) => {
        if (saving) return;
        setSaving(true);
        setErrors({});
        // Resume mode (opened from the list): the record already exists — update it, never re-create.
        const isResume = !!resumeId;
        const payload = buildComparePayload();
        try {
            if (result.type === 'free-seat') {
                if (isResume) {
                    await operaCloudCompareService.update(resumeId, { use_free_seat: true, paid: 'Y', active: true });
                } else {
                    await operaCloudCompareService.create({ ...payload, active: true, use_free_seat: true, paid: 'Y' });
                }
                navigate('/apps/opera-cloud-compare');
                return;
            }

            if (result.type === 'subscription') {
                const subRes = await vaultPaymentService.createSubscription({
                    accountId:       effectiveAccountId,
                    hotelId:         selectedHotel?.hotelid || null,   // same payer the card was saved on
                    paymentMethodId: result.paymentMethodId,
                    stripePriceId:   result.stripePriceId,
                });
                if (isResume) {
                    await operaCloudCompareService.update(resumeId, {
                        paid: 'Y', active: true, use_free_seat: false,
                        stripe_subscription_id: subRes.data?.subscriptionId,
                    });
                } else {
                    await operaCloudCompareService.create({
                        ...payload, active: true, use_free_seat: false, paid: 'Y',
                        stripe_subscription_id: subRes.data?.subscriptionId,
                    });
                }
                navigate('/apps/opera-cloud-compare');
                return;
            }

            if (result.type === 'invoice') {
                // Create the (inactive, unpaid) comparison once, then raise the invoice against it.
                let compareId = createdCompareId;
                if (!compareId) {
                    const createRes = await operaCloudCompareService.create({ ...payload, active: false, use_free_seat: false, paid: 'N' });
                    compareId = createRes.data?.id;
                    setCreatedCompareId(compareId);
                }
                const invRes = await operaCloudCompareService.createInvoice({
                    compareId,
                    accountId:     effectiveAccountId,
                    hotelId:       selectedHotel?.hotelid,
                    stripePriceId: result.stripePriceId,
                    targetType:    result.invoiceTarget,
                    customerEmail: result.invoiceTarget === 'me' ? result.contactEmail : undefined,
                });
                setInvoiceResult(invRes?.data || null);
                setAwaitingPayment(true);
                setSaving(false);
                return;
            }
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || err.message || 'Failed to create comparison' });
            setSaving(false);
        }
    };

    // Superadmin override — bypass Stripe and mark the comparison paid + active. Mirrors the
    // "Mark as paid (admin)" pattern on the sync/vault awaiting-payment screens.
    const confirmMarkPaid = async () => {
        if (saving) return;
        setSaving(true);
        setErrors({});
        try {
            if (resumeId) {
                await operaCloudCompareService.update(resumeId, { paid: 'Y', active: true, use_free_seat: false });
            } else {
                await operaCloudCompareService.create({ ...buildComparePayload(), paid: 'Y', active: true, use_free_seat: false });
            }
            navigate('/apps/opera-cloud-compare');
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || err.message || 'Failed to mark paid' });
            setSaving(false);
        }
    };

    // One source/target slot: credential chip (or select button) + per-hotel override fields.
    const renderInstance = (role, cs) => {
        const credType      = cs.selectedCredential?._entityType;
        const selAboveHotel = credType === 'account' || credType === 'partner';
        const overrideErr   = errors[`${role.toLowerCase()}Override`];
        return (
            <div className="occ-instance-block">
                <div className="occ-instance-row">
                    <div className="occ-instance-role">{role} instance</div>
                    <CredentialSelector
                        marketplaceId={OHIP_MARKETPLACE.id}
                        hotelId={selectedHotel?.hotelid}
                        accountId={selectedAccount}
                        partnerId={selectedPartner}
                        value={cs.selectedCredential}
                        // Per-hotel fields for a chain/partner credential, asked in the picker with Save.
                        overridesInPicker
                        overrideDefs={cs.overrideDefs.length ? cs.overrideDefs : undefined}
                        overrides={cs.overrideValues}
                        overridesTitle="Per-Hotel Overrides"
                        onChange={(sel, values) => (sel ? handleSelect(cs, sel, values) : handleRemove(cs))}
                        canEdit={isSuperAdmin}
                        title={null}
                        subtitle="Choose an existing OHIP credential from the partner, chain or hotel — or add a new one."
                        open={pickerRole === role.toLowerCase()}
                        onOpenChange={(v) => setPickerRole(v ? role.toLowerCase() : null)}
                    />
                </div>
                {selAboveHotel && cs.overrideDefs.length > 0 && overrideErr && (
                    <span className="ppw-error-msg">{overrideErr}</span>
                )}
            </div>
        );
    };

    const nextDisabled = !csSource.selectedCredential || !csTarget.selectedCredential;

    return (
        <div className="ppw-page">
            <WizardStepper steps={STEPS} currentStep={step} />

            {step === 1 && (
                <WizardHotelStep
                    hotels={hotels}
                    selectedHotel={selectedHotel}
                    onSelect={setSelectedHotel}
                    error={errors.hotel}
                    subtitle="Choose the hotel whose OPERA Cloud configuration you want to compare and repair."
                    onBack={() => navigate('/apps/opera-cloud-compare')}
                    onNext={goToCredentials}
                    loading={hotelsLoading}
                />
            )}

            {step === 2 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Select instances</h2>
                        <p className="ppw-card-subtitle">Pick the source and target OHIP credentials to compare.</p>
                    </div>
                    <div className="ppw-card-body">
                        {errors.creds && <span className="ppw-error-msg" style={{ marginBottom: '1rem', display: 'block' }}>{errors.creds}</span>}
                        <div className="occ-instances">
                            {renderInstance('Source', csSource)}
                            <div className="occ-arrow" aria-hidden="true">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M4 12h15m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            {renderInstance('Target', csTarget)}
                        </div>
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-back" onClick={() => setStep(1)}>← Back</button>
                        <button className="ppw-btn-next" onClick={goToPayment} disabled={nextDisabled}>Next →</button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <WizardPaymentStep
                    subtitle="A payment is required to activate this comparison."
                    description="Choose how you would like to activate this comparison."
                    awaiting={awaitingPayment}
                    invoiceResult={invoiceResult}
                    awaitingMessage="Your invoice has been emailed. The comparison will be activated once Stripe confirms the payment."
                    onDone={() => navigate('/apps/opera-cloud-compare')}
                    saving={saving}
                    error={errors.submit}
                    hotelId={selectedHotel?.hotelid}
                    accountId={effectiveAccountId}
                    productId={PRODUCT_ID}
                    invoicePrivilege={invoicePrivilege}
                    currentUser={user}
                    isSuperAdmin={isSuperAdmin}
                    paymentService={buildPaymentService()}
                    onSuccess={handlePaymentComplete}
                    onMarkPaid={confirmMarkPaid}
                    markPaidMessage="Mark this comparison as paid manually? This bypasses Stripe and immediately activates it."
                    onBack={() => resumeId ? navigate('/apps/opera-cloud-compare') : setStep(2)}
                />
            )}
        </div>
    );
};

export default OperaCloudCompareWizard;
